// Studio view preference — what the Studio last showed (zoom + centre), which
// containers this person folded, the Container tool's kind and what is in
// focus on the desk, kept PER DEVICE: view state, per person by design (zoom
// and scroll are view state). Where each card lies is NOT here: that is the
// project's shared layout (Studio spec D7, Project BC). Their own `bilda.*`
// keys through the deviceStore seam, never a domain event. Only the impure
// host reads/writes; parse/serialize are pure.

import {
    type DeskState,
    emptyDesk,
    parseDesk,
    serializeDesk,
} from "../components/Studio/studioDesk.js";
import type { Point, StoredView } from "../components/Studio/studioLayout.js";
import type { ContainerKind } from "./containerFacts.js";
import { readDeviceKey, writeDeviceKey } from "./deviceStore.js";

// --- Parsing (pure) ------------------------------------------------------------

const pointOf = (raw: unknown): Point | undefined => {
    if (!Array.isArray(raw) || raw.length !== 2) return undefined;
    const [x, y]: readonly unknown[] = raw;
    return typeof x === "number" &&
        typeof y === "number" &&
        Number.isFinite(x) &&
        Number.isFinite(y)
        ? { x, y }
        : undefined;
};

const parseJson = (raw: string): unknown => {
    try {
        return JSON.parse(raw);
    } catch {
        return undefined;
    }
};

// --- The last view (zoom + the canvas point at the centre) ---------------------
// Stored as JSON `{ "zoom": z, "centre": [x, y] }`; anything else reads as
// "nothing stored" (the Studio then fits every card in).

const VIEW_KEY = "bilda.studioView";

export const parseStudioView = (raw: string | null): StoredView | null => {
    if (raw === null) return null;
    const value = parseJson(raw);
    if (typeof value !== "object" || value === null || Array.isArray(value))
        return null;
    const zoom = "zoom" in value ? value.zoom : undefined;
    const centre = pointOf("centre" in value ? value.centre : undefined);
    return typeof zoom === "number" &&
        Number.isFinite(zoom) &&
        zoom > 0 &&
        centre !== undefined
        ? { zoom, centre }
        : null;
};

export const serializeStudioView = (view: StoredView): string =>
    JSON.stringify({ zoom: view.zoom, centre: [view.centre.x, view.centre.y] });

export const readStudioView = (): StoredView | null => {
    try {
        return parseStudioView(readDeviceKey(VIEW_KEY));
    } catch {
        return null;
    }
};

export const writeStudioView = (view: StoredView): void => {
    try {
        writeDeviceKey(VIEW_KEY, serializeStudioView(view));
    } catch {
        // Not kept on this device; the next visit fits every card in.
    }
};

// --- The Container tool's kind (pen B4: "Module, default, remembered") ---------
// Stored as the bare kind; anything else reads as the default, Module.

const CONTAINER_KIND_KEY = "bilda.studioContainerKind";

const parseContainerKind = (raw: string | null): ContainerKind =>
    raw === "course" ? "course" : "module";

export const readContainerKind = (): ContainerKind => {
    try {
        return parseContainerKind(readDeviceKey(CONTAINER_KIND_KEY));
    } catch {
        return "module";
    }
};

export const writeContainerKind = (kind: ContainerKind): void => {
    try {
        writeDeviceKey(CONTAINER_KIND_KEY, kind);
    } catch {
        // Not kept on this device; the tool starts at Module next time.
    }
};

// --- Sets of card ids: folded containers, cards off the canvas ----------------
// Stored as a JSON array of ids; anything else reads as the empty set.
//   - folded: the containers this person folded (view state, per person;
//     pen "Studio – detail design" §3);
//   - hidden: the cards taken off the canvas ("Remove from canvas"; the
//     material stays in the Library). The same deliberate shortcut as the
//     positions above: per device until the canvas is shared project state.

const FOLDED_KEY = "bilda.studioFolded";

export const parseIdSet = (raw: string | null): ReadonlySet<string> => {
    if (raw === null) return new Set();
    const value = parseJson(raw);
    return Array.isArray(value)
        ? new Set(value.filter((v): v is string => typeof v === "string"))
        : new Set();
};

const readIdSet = (key: string): ReadonlySet<string> => {
    try {
        return parseIdSet(readDeviceKey(key));
    } catch {
        return new Set();
    }
};

const writeIdSet = (key: string, ids: ReadonlySet<string>): void => {
    try {
        writeDeviceKey(key, JSON.stringify([...ids]));
    } catch {
        // Not kept on this device; this visit still shows it.
    }
};

export const readFoldedContainers = (): ReadonlySet<string> =>
    readIdSet(FOLDED_KEY);
export const writeFoldedContainers = (ids: ReadonlySet<string>): void =>
    writeIdSet(FOLDED_KEY, ids);

// --- What is in focus on the desk (studioDesk.ts) ------------------------------
// View state, per person, kept on this device like the zoom: after a reload
// the same level is in focus. (".path": the earlier key held several open
// things side by side, which focus no longer has.)

const DESK_KEY = "bilda.studioDesk.path";

export const readStudioDesk = (): DeskState => {
    try {
        return parseDesk(readDeviceKey(DESK_KEY));
    } catch {
        return emptyDesk;
    }
};

export const writeStudioDesk = (state: DeskState): void => {
    try {
        writeDeviceKey(DESK_KEY, serializeDesk(state));
    } catch {
        // Not kept on this device; this visit still shows it open.
    }
};
